import { useState, useEffect, useCallback } from 'react';
import { useCopy, fill } from '../../i18n';
import { useProject } from '../../contexts/ProjectContext';
import { useUIState } from '../../contexts/UIStateContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { MODULE_FEATURE_MAP, FEATURE_CONFIG, PLAN_HIERARCHY, getMinPlanForFeature } from '../../utils/featureConfig';
import { describeError } from '../../utils/errorMessages';
import { avatarColorFor, initialsFor } from '@/lib/avatarPalette';
import PricingPage from '../billing/PricingPage';
import analytics from '../../services/analyticsService';
import { cn } from '@/lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AddButton } from '@/components/ui/add-button';
import { toast } from 'sonner';
import ConfirmModal from '../common/ConfirmModal';
import AIProjectModal from '../modals/AIProjectModal';
import OrgInviteBanner from '../organization/OrgInviteBanner';
import OrgProjectShareModal from '../organization/OrgProjectShareModal';
import { NotificationBell } from '../notifications/NotificationBell';
import TrialBanner from '../billing/TrialBanner';
import SupportPanel from '../support/SupportPanel';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useProjectFolders } from '../../contexts/ProjectFolderContext';
import { groupProjectsByFolder, foldersForScope } from '../../utils/projectGrouping';
import type { ProjectFolder, ProjectWithRole } from '../../types/database';

interface SidebarProps {
  onOpenSearch?: () => void;
  mobileSidebarOpen?: boolean;
  onMobileClose?: () => void;
}

interface UpgradeModalState {
  feature: string;
  displayName: string;
  description: string;
  minPlan: string;
}

import {
  LayoutDashboard,
  Search,
  FolderKanban,
  Plus,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Star,
  MoreVertical,
  Trash2,
  Edit3,
  Copy,
  Loader2,
  LogOut,
  Lock,
  ArrowUpCircle,
  Briefcase,
  BookOpen,
  Sun,
  Moon,
  FolderInput,
  FolderOutput,
  Folder,
  FolderPlus,
  ListChecks,
  Building2,
  MessageCircle,
  X,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const SECTIONS_STORAGE_KEY = 'meugantt_sidebar_sections';

/**
 * Badge de contagem, um so para header de secao e linha de pasta: os dois
 * contam a mesma coisa (quantos projetos ha ali dentro) e nao ha motivo para
 * pesarem diferente. Subiu para o piso de 11px da escala; estava abaixo dele.
 */
const COUNT_BADGE_CLASS =
  'text-[11px] font-semibold text-white/40 bg-white/10 px-1.5 py-px rounded-[10px] ml-1.5';

/**
 * O trilho vertical que segura os filhos de uma pasta.
 *
 * E o `SidebarMenuSub` do shadcn traduzido para a paleta da nossa sidebar: uma
 * linha de 1px a esquerda mais recuo, em vez de so empurrar o texto. Indentacao
 * sozinha some quando o nome do projeto e longo e quebra a leitura de "quem esta
 * dentro de quem"; o trilho responde isso sem o usuario precisar comparar
 * margens.
 *
 * `ml-[23px]` poe o trilho no CENTRO do icone da pasta: 8px de `mx-2` da linha,
 * mais 8px de `px-2`, mais metade dos 14px do icone. Era `ml-4` (16px), que
 * encostava na borda esquerda do icone em vez do meio dele, e e a diferenca
 * entre parecer alinhado de proposito e parecer quase alinhado.
 */
const FOLDER_RAIL_CLASS =
  'mr-2 ml-[23px] flex flex-col gap-0.5 border-l border-white/10 pl-2 py-0.5';

/**
 * Acao que so aparece quando o mouse passa pela linha.
 *
 * `mouse:` antes do `opacity-0`: sem o prefixo, o controle fica invisivel PARA
 * SEMPRE em quem nao tem mouse. A sidebar renderiza fixa em qualquer largura
 * acima de 768px, entao num tablet Android ou num iPad em retrato esses
 * botoes existiam a 0% de opacidade sem gesto nenhum que os revelasse.
 *
 * `opacity-0` e nao `hidden`: `display:none` tira o botao da ordem de tabulacao
 * e some com a acao inteira para quem navega por teclado. Transparente ele
 * continua focavel, e `focus-visible` o traz de volta a vista no Tab.
 */
const HOVER_ACTION_CLASS =
  'flex shrink-0 items-center p-1 rounded-[var(--radius-sm)] bg-transparent border-none cursor-pointer text-white/50 mouse:opacity-0 transition-opacity duration-150 mouse:group-hover:opacity-100 focus-visible:opacity-100 hover:bg-white/15 hover:text-[var(--sidebar-text)]';

/**
 * Modulos que renderizam tarefas com `data-task-id`, ou seja, onde faz sentido
 * destacar e rolar ate a tarefa vinda de uma notificacao. Se o usuario ja esta
 * em um deles, o clique na notificacao nao o tira de la.
 */
const MODULOS_COM_TAREFA = new Set(['gantt', 'kanban', 'schedule', 'mytasks', 'calendar']);

const Sidebar = ({ onOpenSearch, mobileSidebarOpen, onMobileClose }: SidebarProps) => {
  const t = useCopy().shell;
  const tShell = useCopy().common.appShell;
  const isMobile = useIsMobile();

  /**
   * A gaveta fechada precisa sair do alcance de VERDADE, e nao so da vista.
   *
   * No celular ela e `-translate-x-full`, ou seja, esta fora da tela mas
   * continua no DOM, sem `inert` e sem `aria-hidden`. Resultado: os dez
   * controles dela seguiam na ordem de tabulacao e no leitor de tela. Pior, no
   * celular o hamburguer nem existe (App.tsx renderiza com `!isMobile`), entao
   * a gaveta NUNCA pode ser aberta ali: era um menu inalcancavel que mesmo
   * assim engolia o foco de quem navega por teclado.
   *
   * `inert` remove a subarvore inteira do foco, do leitor de tela e do clique,
   * que e exatamente o que "esta fechada" deveria significar. So no celular: no
   * desktop a barra e a navegacao e tem que continuar viva.
   *
   * Entra por spread porque o React 18 nao conhece o atributo na tipagem. O
   * valor e string vazia, que e a forma booleana em HTML.
   */
  const gavetaForaDeAlcance = isMobile && !mobileSidebarOpen;
  const atributosInertes = (gavetaForaDeAlcance ? { inert: '' } : {}) as Record<string, string>;
  const {
    projects,
    currentProjectId,
    switchProject,
    focusTask,
    createProject,
    deleteProject,
    renameProject,
    duplicateProject,
    toggleProjectFavorite,
    moveProjectToFolder,
    loading,
    saving,
    loadProjects
  } = useProject();
  const { currentModule, changeModule } = useUIState();

  const { logout, user, profile } = useAuth();
  const { folders, createFolder, renameFolder, deleteFolder } = useProjectFolders();
  const { resolvedTheme, setTheme } = useTheme();
  const { hasFeature, canCreateProject, isAtProjectLimit, limits, usage, isSystemAdmin, planName, loading: subLoading } = useSubscription();
  const { organizations, moveProjectToOrg } = useOrganization();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem('user-preferences') || '{}');
      return prefs?.sidebarCollapsed || false;
    } catch { return false; }
  });
  const [showNewProjectInput, setShowNewProjectInput] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDeleteProject, setConfirmDeleteProject] = useState<string | null>(null);
  const [upgradeModal, setUpgradeModal] = useState<UpgradeModalState | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [shareProjectId, setShareProjectId] = useState<{ id: string; name: string } | null>(null);
  const [confirmRemoveFromOrg, setConfirmRemoveFromOrg] = useState<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);

  // Pastas. `creatingFolderIn` guarda o ESCOPO em que a pasta vai nascer
  // (null = pasta pessoal, id da org = pasta da organizacao), e nao um booleano,
  // porque a sidebar mostra varios escopos ao mesmo tempo e o input precisa
  // aparecer embaixo do certo. `false` significa "nao estou criando nenhuma".
  const [creatingFolderIn, setCreatingFolderIn] = useState<string | null | false>(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<ProjectFolder | null>(null);

  // Estado de seções colapsáveis (favoritos, pessoal, cada org)
  const [sectionState, setSectionState] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY) || '{}');
    } catch { return {}; }
  });

  const isSectionExpanded = (key: string) => sectionState[key] !== false; // default expanded

  const toggleSectionState = useCallback((key: string) => {
    setSectionState(prev => {
      const next = { ...prev, [key]: prev[key] === false };
      try { localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /**
   * Abre uma secao sem depender do estado anterior.
   *
   * Existe porque guardar um projeto numa pasta fechada esconderia justamente o
   * que o usuario acabou de fazer: ele clica em "adicionar", o contador sobe e
   * nada aparece. Toda acao que poe conteudo la dentro abre a gaveta.
   */
  const expandSection = useCallback((key: string) => {
    setSectionState(prev => {
      if (prev[key] !== false) return prev;
      const next = { ...prev, [key]: true };
      try { localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Listener para quando o modal de IA dispara "criar projeto em branco"
  useEffect(() => {
    const handler = () => setShowNewProjectInput(true);
    window.addEventListener('create-blank-project', handler);
    return () => window.removeEventListener('create-blank-project', handler);
  }, []);

  // Verificar se módulo está bloqueado
  const isModuleLocked = (moduleId: string) => {
    if (isSystemAdmin || subLoading) return false;
    const featureKey = MODULE_FEATURE_MAP[moduleId];
    if (!featureKey) return false;
    return !hasFeature(featureKey);
  };

  const handleModuleClick = (moduleId: string) => {
    if (isModuleLocked(moduleId)) {
      const featureKey = MODULE_FEATURE_MAP[moduleId] || '';
      const config = FEATURE_CONFIG[featureKey];
      const minPlan = getMinPlanForFeature(featureKey);
      analytics.trackFeatureGateLocked(moduleId, planName || 'free');
      setUpgradeModal({
        feature: featureKey,
        displayName: config?.displayName || moduleId,
        description: config?.description || '',
        minPlan: PLAN_HIERARCHY[minPlan]?.displayName || 'Starter'
      });
      return;
    }
    changeModule(moduleId);
    // No celular a sidebar e gaveta sobreposta: sem isto, escolher um modulo
    // trocava a tela POR BAIXO e a gaveta continuava cobrindo 260px, entao o
    // usuario tocava, nada parecia acontecer, e ele precisava adivinhar que o
    // caminho de volta era tocar fora. Nao ha guarda de viewport porque no
    // desktop `onMobileClose` nao faz nada: a gaveta nem existe.
    onMobileClose?.();
  };

  const handleNewProjectClick = () => {
    if (!isSystemAdmin && !canCreateProject()) {
      analytics.trackProjectLimitHit(planName || 'free');
      const projectLimit = limits.projects === -1 ? t.project.unlimited : limits.projects;
      setUpgradeModal({
        feature: 'project_limit',
        displayName: t.project.limitTitle,
        description: fill(t.project.limitBodyCount, { limit: projectLimit }),
        minPlan: 'Starter'
      });
      return;
    }
    setShowAIModal(true);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      await createProject(newProjectName.trim());
      setNewProjectName('');
      setShowNewProjectInput(false);
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'PLAN_LIMIT_REACHED') {
        setUpgradeModal({
          feature: 'project_limit',
          displayName: t.project.limitTitle,
          description: t.project.limitBody,
          minPlan: 'Starter'
        });
      } else {
        console.error(t.project.createError, err);
      }
    }
  };

  const handleDeleteProject = (projectId: string) => {
    setConfirmDeleteProject(projectId);
  };

  const executeDeleteProject = async () => {
    if (!confirmDeleteProject) return;
    try {
      await deleteProject(confirmDeleteProject);
    } catch (err) {
      console.error(t.project.deleteError, err);
    } finally {
      setConfirmDeleteProject(null);
    }
  };

  const executeRemoveFromOrg = async () => {
    if (!confirmRemoveFromOrg) return;
    try {
      await moveProjectToOrg(confirmRemoveFromOrg, null);
      await loadProjects();
    } catch (err) {
      console.error(t.project.removeFromOrgError, err);
    } finally {
      setConfirmRemoveFromOrg(null);
    }
  };

  const handleStartRename = (project: ProjectWithRole) => {
    setEditingProjectId(project.id);
    setEditingName(project.name);
  };

  const handleDuplicateProject = async (projectId: string) => {
    try {
      await duplicateProject(projectId);
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'PLAN_LIMIT_REACHED') {
        setUpgradeModal({
          feature: 'project_limit',
          displayName: t.project.limitTitle,
          description: t.project.limitBody,
          minPlan: 'Starter'
        });
      } else {
        console.error(t.project.duplicateError, err);
      }
    }
  };

  const handleRename = async () => {
    if (!editingName.trim() || !editingProjectId) return;
    try {
      await renameProject(editingProjectId, editingName.trim());
    } catch (err) {
      console.error(t.project.renameError, err);
    }
    setEditingProjectId(null);
    setEditingName('');
  };

  const handleToggleFavorite = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    toggleProjectFavorite(projectId);
  };

  // ===== Pastas =====

  /**
   * Erro de pasta vai para a TELA, nao so para o console.
   *
   * Enquanto so havia `console.error`, uma falha no banco (RLS, cache de schema
   * do PostgREST, sessao expirada) chegava ao usuario como "cliquei e nao
   * aconteceu nada". Toast e a diferenca entre um bug reportavel e um recurso
   * que parece quebrado sem motivo.
   */
  const reportFolderError = (message: string, err: unknown) => {
    console.error(message, err);
    const detail = describeError(err);
    toast.error(detail ? `${message} ${detail}` : message);
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    const scope = creatingFolderIn;
    if (!name || scope === false) {
      setCreatingFolderIn(false);
      setNewFolderName('');
      return;
    }

    try {
      const folder = await createFolder(name, scope);
      // Sem `folder`, a sessao caiu no meio do caminho: o contexto so cria pasta
      // com usuario carregado, e devolver nulo em silencio seria o mesmo "nao
      // aconteceu nada" de antes.
      if (!folder) {
        toast.error(t.folder.sessionError);
        return;
      }
      analytics.trackFolderCreated(scope !== null);
      toast.success(fill(t.folder.created, { name: folder.name }));
    } catch (err) {
      reportFolderError(t.folder.createError, err);
    } finally {
      setCreatingFolderIn(false);
      setNewFolderName('');
    }
  };

  const handleRenameFolder = async () => {
    const id = editingFolderId;
    const name = editingFolderName.trim();
    if (!id) return;

    setEditingFolderId(null);
    setEditingFolderName('');

    // Nome vazio nao apaga a pasta: apagar e uma acao com confirmacao propria,
    // e um blur acidental no campo nao pode virar exclusao.
    const current = folders.find(f => f.id === id);
    if (!name || !current || current.name === name) return;

    try {
      await renameFolder(id, name);
    } catch (err) {
      reportFolderError(t.folder.renameError, err);
    }
  };

  const executeDeleteFolder = async () => {
    if (!confirmDeleteFolder) return;
    try {
      await deleteFolder(confirmDeleteFolder.id);
      // Os projetos ficaram com folder_id nulo no banco (ON DELETE SET NULL).
      // Sem este refetch a lista local continuaria apontando para uma pasta que
      // nao existe mais; o agrupamento aguenta (cai em "sem pasta"), mas o
      // estado ficaria mentindo ate o proximo carregamento.
      await loadProjects();
    } catch (err) {
      reportFolderError(t.folder.deleteError, err);
    } finally {
      setConfirmDeleteFolder(null);
    }
  };

  /**
   * Guarda o projeto numa pasta, com volta.
   *
   * O undo vive no toast, e nao num Ctrl+Z: o gesto acontece na sidebar, longe
   * do foco de teclado, e quem acabou de mover precisa da saida no mesmo lugar
   * onde recebeu a confirmacao. Sem ela, experimentar a organizacao da carteira
   * custa caro e o usuario simplesmente nao experimenta.
   */
  const handleMoveToFolder = async (projectId: string, folderId: string | null) => {
    const previous = projects.find(p => p.id === projectId)?.folder_id ?? null;
    try {
      await moveProjectToFolder(projectId, folderId);
      if (folderId) {
        expandSection(`folder_${folderId}`);
        analytics.trackProjectMovedToFolder();
      }

      const destino = folderId ? folders.find(f => f.id === folderId)?.name : null;
      toast.success(
        destino ? fill(t.folder.moved, { name: destino }) : t.folder.movedOut,
        {
          action: {
            label: t.folder.undo,
            onClick: () => { void moveProjectToFolder(projectId, previous); },
          },
        }
      );
    } catch (err) {
      reportFolderError(t.folder.moveError, err);
    }
  };

  /**
   * Separar projetos por seção.
   *
   * Favoritos ATALHA, não muda de endereço: o projeto continua onde mora (na
   * pasta do cliente, na organização, na lista pessoal) e ganha uma segunda
   * aparição no topo. Era o contrário, e favoritar arrancava o projeto da pasta
   * em que o usuário tinha acabado de guardá-lo, desfazendo a organização dele
   * como efeito colateral de um clique na estrela.
   *
   * Por isso os outros filtros NÃO excluem mais `isFavorited`: a duplicata é
   * intencional aqui, diferente da duplicata dentro de um mesmo escopo, que
   * continua sendo defeito (é o que `groupProjectsByFolder` evita).
   */
  const favoriteProjects = projects.filter(p => p.isFavorited);
  const personalProjects = projects.filter(p => !p.organization_id && p.userRole === 'owner');
  const sharedProjects = projects.filter(p => !p.organization_id && p.userRole !== 'owner');
  const getOrgProjects = (orgId: string) => projects.filter(p => p.organization_id === orgId);
  const activeOrgs = organizations.filter(org => projects.some(p => p.organization_id === org.id));

  // Identidade do rodape. Sem nome cadastrado, o comeco do e-mail e melhor do
  // que um espaco vazio: ainda identifica a conta em que a pessoa esta.
  const accountName = profile?.full_name?.trim() || profile?.email?.split('@')[0] || t.nav.account;
  const accountPlan = PLAN_HIERARCHY[(planName || 'free') as keyof typeof PLAN_HIERARCHY]?.displayName || 'Free';

  // Calcular info de limite de projetos
  const projectLimit = limits.projects;
  const projectCount = usage.projects;
  const showProjectLimit = projectLimit !== -1 && !isSystemAdmin;

  /**
   * Bloco "Mover para pasta" DENTRO do menu do projeto, sem submenu aninhado.
   *
   * Era `DropdownMenuSub` e virou lista plana sob um rotulo. Submenu do Radix so
   * abre no hover do gatilho: no celular, que e ~90% das sessoes, o gesto nao
   * existe, e num painel de 260px o conteudo sai espremido. Plano custa algumas
   * linhas a mais no menu e funciona em todo lugar.
   *
   * So oferece pastas do MESMO escopo do projeto (as da org dele, ou as minhas
   * pessoais se ele nao esta em org nenhuma). O banco recusa o resto pelo trigger
   * `enforce_project_folder_scope`; listar aqui o que la sera negado so geraria
   * erro na cara do usuario.
   *
   * O bloco aparece SEMPRE, mesmo sem nenhuma pasta criada: escondido, ele fazia
   * a feature inteira parecer inexistente para quem a procurava aqui.
   */
  const renderMoveToFolderMenu = (project: ProjectWithRole) => {
    const scopeFolders = foldersForScope(folders, project.organization_id, user?.id);

    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[length:var(--font-xs)] font-semibold uppercase tracking-[0.5px] text-[var(--color-text-muted)]">
          {t.folder.moveTo}
        </DropdownMenuLabel>
        {scopeFolders.map(folder => (
          <DropdownMenuItem
            key={folder.id}
            disabled={project.folder_id === folder.id}
            onClick={() => handleMoveToFolder(project.id, folder.id)}
          >
            <Folder size={14} className="mr-2" />
            <span className="truncate">{folder.name}</span>
          </DropdownMenuItem>
        ))}
        {project.folder_id && (
          <DropdownMenuItem onClick={() => handleMoveToFolder(project.id, null)}>
            <FolderOutput size={14} className="mr-2" /> {t.folder.removeFrom}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            setCreatingFolderIn(project.organization_id);
            setNewFolderName('');
          }}
        >
          <FolderPlus size={14} className="mr-2" /> {t.folder.new}
        </DropdownMenuItem>
      </>
    );
  };

  /**
   * Item de projeto. `inFolder` marca que ele esta dentro do trilho de uma pasta.
   *
   * Dentro do trilho o item perde o recuo proprio e a barra lateral de ativo: o
   * recuo ja vem do container (FOLDER_RAIL_CLASS) e uma segunda barra vertical
   * colada no trilho viraria duas linhas paralelas brigando. O ativo se resolve
   * so com fundo, como no shadcn.
   */
  const renderProjectItem = (project: ProjectWithRole, inFolder = false) => (
    <div
      key={project.id}
      data-project-item
      className={cn(
        "flex items-center gap-2 cursor-pointer transition-colors duration-150 relative hover:bg-[var(--sidebar-hover)] [&_svg]:text-[var(--sidebar-text-muted)] [&_svg]:shrink-0 group",
        collapsed
          ? "justify-center p-2.5"
          : inFolder
            ? "h-8 rounded-[var(--radius-md)] px-2"
            : "mx-2 h-8 rounded-[var(--radius-md)] px-2",
        // Ativo se resolve so com FUNDO, dentro e fora da pasta. Havia uma
        // barra de 3px so fora dela, e como favoritar agora replica o projeto,
        // dava para ver os dois tratamentos do mesmo item na mesma tela.
        currentProjectId === project.id && "bg-[var(--sidebar-active)] [&_svg]:text-[var(--sidebar-accent)]"
      )}
      onClick={() => {
        switchProject(project.id);
        if (currentModule === 'home' || currentModule === 'settings' || currentModule === 'portfolio' || currentModule === 'wiki' || currentModule === 'mytasks') {
          changeModule('gantt');
        }
        // Mesma razao do handleModuleClick: escolher projeto e navegar, e a
        // gaveta precisa sair da frente do resultado.
        onMobileClose?.();
      }}
      title={project.name}
    >
      {!collapsed && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                "bg-transparent border-none cursor-pointer p-0.5 rounded-[var(--radius-sm)] flex items-center shrink-0 transition-[opacity,color] duration-150 ease-in-out",
                project.isFavorited
                  ? "opacity-100 text-[var(--color-warning)] [&_svg]:fill-[var(--color-warning)] [&_svg]:text-[var(--color-warning)]"
                  : "mouse:opacity-0 mouse:group-hover:opacity-100 focus-visible:opacity-100 text-white/40 hover:text-[var(--color-warning)]"
              )}
              onClick={(e) => handleToggleFavorite(e, project.id)}
            >
              <Star size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top"><p>{project.isFavorited ? t.project.removeFromFavorites : t.project.addToFavorites}</p></TooltipContent>
        </Tooltip>
      )}
      {collapsed && <FolderKanban size={18} />}
      {!collapsed && (
        <>
          {editingProjectId === project.id ? (
            <Input
              type="text"
              className="flex-1 h-auto px-2 py-1 bg-white/10 border-[var(--sidebar-accent)] text-[var(--sidebar-text)] text-[length:var(--font-base)] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') {
                  setEditingProjectId(null);
                  setEditingName('');
                }
              }}
              onBlur={handleRename}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <span className={cn("min-w-0 flex-1 truncate text-[length:var(--font-base)] text-white/90", currentProjectId === project.id && "text-[var(--sidebar-text)] font-medium")}>{project.name}</span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="mouse:opacity-0 mouse:group-hover:opacity-100 focus-visible:opacity-100 bg-transparent border-none text-white/50 cursor-pointer p-1 rounded-[var(--radius-sm)] flex items-center hover:bg-white/15 hover:text-[var(--sidebar-text)]"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              <DropdownMenuItem onClick={() => toggleProjectFavorite(project.id)}>
                <Star size={14} className="mr-2" /> {project.isFavorited ? t.project.unfavorite : t.project.favorite}
              </DropdownMenuItem>
              {project.userRole === 'owner' && (
                <DropdownMenuItem onClick={() => handleStartRename(project)}>
                  <Edit3 size={14} className="mr-2" /> {t.project.rename}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleDuplicateProject(project.id)}>
                <Copy size={14} className="mr-2" /> {t.project.duplicate}
              </DropdownMenuItem>
              {project.userRole === 'owner' && organizations.length > 0 && !project.organization_id && (
                <DropdownMenuItem onClick={() => setShareProjectId({ id: project.id, name: project.name })}>
                  <FolderInput size={14} className="mr-2" /> {t.project.moveToOrg}
                </DropdownMenuItem>
              )}
              {project.userRole === 'owner' && project.organization_id && (
                <DropdownMenuItem onClick={() => setConfirmRemoveFromOrg(project.id)}>
                  <FolderOutput size={14} className="mr-2" /> {t.project.removeFromOrgShort}
                </DropdownMenuItem>
              )}
              {renderMoveToFolderMenu(project)}
              {project.userRole === 'owner' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-[var(--color-danger)] focus:text-[var(--color-danger)]" onClick={() => handleDeleteProject(project.id)}>
                    <Trash2 size={14} className="mr-2" /> {t.project.delete}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );

  /**
   * Menu que guarda um projeto nesta pasta.
   *
   * Um nivel so, NUNCA submenu aninhado. Submenu do Radix abre no hover do
   * gatilho: no dedo isso nao existe, e num painel de 260px o conteudo sai
   * espremido ou cortado. Como ~90% das sessoes sao de celular, aninhar aqui
   * seria desenhar para a minoria.
   *
   * A lista ja vem filtrada pelo escopo, entao ela so oferece o que o banco
   * aceita: projeto de organizacao nao entra em pasta pessoal. Oferecer e depois
   * recusar com erro seria pior do que nao oferecer.
   */
  const renderAddProjectMenu = (
    folder: ProjectFolder,
    candidates: ProjectWithRole[],
    trigger: React.ReactNode
  ) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {candidates.map(p => (
          <DropdownMenuItem key={p.id} onClick={() => handleMoveToFolder(p.id, folder.id)}>
            <FolderKanban size={14} className="mr-2" />
            <span className="truncate">{p.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  /**
   * Linha de pasta, no padrao de sidebar do shadcn (o mesmo da sidebar do
   * claude.ai, que foi a referencia pedida):
   *
   *   - chevron a DIREITA, que rotaciona 90 graus ao abrir, em vez de trocar de
   *     icone. Rotacionar mostra que e o mesmo objeto mudando de estado;
   *     trocar `ChevronRight` por `ChevronDown` pisca e parece outro botao.
   *   - `+` na propria linha da pasta, revelado no hover (padrao do Notion). E o
   *     conserto do "nao sei como coloca um projeto dentro": a acao mora onde o
   *     olho ja esta, e nao num menu do outro lado da tela.
   *   - altura h-8 e cantos arredondados, para o item respirar como no shadcn.
   */
  const renderFolderRow = (folder: ProjectFolder, count: number, scopeProjects: ProjectWithRole[]) => {
    const expanded = isSectionExpanded(`folder_${folder.id}`);
    const isEditing = editingFolderId === folder.id;
    const candidates = scopeProjects.filter(p => p.folder_id !== folder.id);

    return (
      <div
        key={folder.id}
        data-folder-item
        className="group mx-2 flex h-8 items-center gap-2 rounded-[var(--radius-md)] px-2 cursor-pointer select-none transition-colors duration-150 hover:bg-[var(--sidebar-hover)] [&_svg]:shrink-0"
        onClick={() => !isEditing && toggleSectionState(`folder_${folder.id}`)}
        title={folder.name}
      >
        <Folder size={14} className="text-[var(--sidebar-text-muted)]" />

        {isEditing ? (
          <Input
            type="text"
            className="flex-1 h-7 px-1.5 py-0 bg-white/10 border-[var(--sidebar-accent)] text-[var(--sidebar-text)] text-[length:var(--font-base)] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            value={editingFolderName}
            onChange={(e) => setEditingFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameFolder();
              if (e.key === 'Escape') {
                setEditingFolderId(null);
                setEditingFolderName('');
              }
            }}
            onBlur={handleRenameFolder}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <span className="min-w-0 flex-1 truncate text-[length:var(--font-base)] text-white/80">
            {folder.name}
          </span>
        )}

        <span className={COUNT_BADGE_CLASS}>{count}</span>

        {/* Revelar no hover e `opacity-0`, NUNCA `hidden`.
            `hidden` vira `display:none`, que tira o botao da ordem de tabulacao:
            quem navega por teclado simplesmente perde as duas acoes da pasta.
            `opacity-0` esconde sem remover, e `focus-visible` traz de volta
            assim que o Tab chega nele. E tambem o padrao do resto do arquivo
            (estrela de favorito e "..." do projeto). */}
        {candidates.length > 0 && renderAddProjectMenu(
          folder,
          candidates,
          <button
            aria-label={t.folder.addProject}
            title={t.folder.addProject}
            className={HOVER_ACTION_CLASS}
            onClick={(e) => e.stopPropagation()}
          >
            <Plus size={16} />
          </button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label={t.folder.actions}
              className={HOVER_ACTION_CLASS}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[160px]">
            <DropdownMenuItem onClick={() => {
              setEditingFolderId(folder.id);
              setEditingFolderName(folder.name);
            }}>
              <Edit3 size={14} className="mr-2" /> {t.folder.rename}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-[var(--color-danger)] focus:text-[var(--color-danger)]"
              onClick={() => setConfirmDeleteFolder(folder)}
            >
              <Trash2 size={14} className="mr-2" /> {t.folder.delete}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ChevronRight
          size={14}
          className={cn('text-white/40 transition-transform duration-150', expanded && 'rotate-90')}
        />
      </div>
    );
  };

  /**
   * Os projetos de um escopo, agrupados pelas pastas daquele escopo.
   *
   * Sem pasta nenhuma criada, devolve a lista lisa de sempre: quem nunca usar a
   * feature nao ve diferenca na sidebar. O rotulo "Sem pasta" so nasce quando ja
   * existe pelo menos uma pasta para contrastar com ele.
   */
  const renderScopeProjects = (scopeProjects: ProjectWithRole[], organizationId: string | null) => {
    const scopeFolders = foldersForScope(folders, organizationId, user?.id);
    const { groups, unfiled, hasFolders } = groupProjectsByFolder(scopeProjects, scopeFolders);
    const creating = creatingFolderIn === organizationId;

    // Pasta vazia continua na tela: recem-criada, ela some antes de o usuario
    // conseguir guardar o primeiro projeto nela se filtrarmos por conteudo.
    const emptyFolders = scopeFolders.filter(f => !groups.some(g => g.folder.id === f.id));

    return (
      // O escopo fica visivel no DOM porque ele decide o que pode entrar em que
      // pasta, e sem isso nao da para escrever teste honesto: o primeiro projeto
      // da sidebar pode ser de uma organizacao, e pasta pessoal nao aceita ele.
      <div className="flex flex-col" data-folder-scope={organizationId ?? 'personal'}>
        {groups.map(({ folder, projects: inFolder }) => (
          <div key={folder.id}>
            {renderFolderRow(folder, inFolder.length, scopeProjects)}
            {isSectionExpanded(`folder_${folder.id}`) && (
              <div className={FOLDER_RAIL_CLASS}>
                {inFolder.map(p => renderProjectItem(p, true))}
              </div>
            )}
          </div>
        ))}

        {emptyFolders.map(folder => (
          <div key={folder.id}>
            {renderFolderRow(folder, 0, scopeProjects)}
            {/* Pasta recem-criada e onde o usuario procura "como ponho um
                projeto aqui". O convite vive DENTRO do trilho, como se fosse o
                primeiro filho: assim o trilho nunca aparece cortado, e a acao
                esta no lugar para onde o olho ja desceu. */}
            {isSectionExpanded(`folder_${folder.id}`) && (
              <div className={FOLDER_RAIL_CLASS}>
                {scopeProjects.length > 0 ? (
                  renderAddProjectMenu(
                    folder,
                    scopeProjects,
                    <button
                      className="flex h-8 w-full items-center gap-2 rounded-[var(--radius-md)] px-2 bg-transparent border-none cursor-pointer text-left text-[length:var(--font-sm)] text-white/40 hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-accent)] [&_svg]:shrink-0"
                    >
                      <Plus size={14} />
                      <span className="truncate">{t.folder.addProject}</span>
                    </button>
                  )
                ) : (
                  <div className="flex h-8 items-center px-2 text-[length:var(--font-sm)] text-white/40">
                    {t.folder.empty}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {hasFolders && unfiled.length > 0 && (
          <div className="px-4 pt-3 pb-1 text-[length:var(--font-xs)] font-semibold uppercase text-white/35 tracking-[0.5px]">
            {t.folder.unfiled}
          </div>
        )}
        {unfiled.map(p => renderProjectItem(p))}

        {/* Campo e botao dividem a mesma caixa (mx-2, h-8): o input nasce
            exatamente onde o botao estava, sem a linha pular no clique. */}
        {creating ? (
          <div className="mx-2 mt-1">
            <Input
              type="text"
              placeholder={t.folder.namePlaceholder}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') {
                  setCreatingFolderIn(false);
                  setNewFolderName('');
                }
              }}
              onBlur={handleCreateFolder}
              autoFocus
              className="w-full h-7 px-2 bg-white/10 border-white/20 text-[var(--sidebar-text)] text-[length:var(--font-sm)] placeholder:text-white/40 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--sidebar-accent)]"
            />
          </div>
        ) : (
          <div className="mx-2 mt-1">
            {/* Largura total e altura h-8: o botao passa a ocupar a mesma faixa
                das linhas de pasta e de projeto, em vez de flutuar como uma
                pastilha solta no meio da coluna. */}
            <AddButton
              data-testid="sidebar-new-folder"
              icon={<FolderPlus size={14} className="text-[var(--sidebar-accent)]" />}
              className="w-full h-8 justify-start px-2 border-white/20 text-white/50 hover:border-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent)]"
              onClick={() => {
                setCreatingFolderIn(organizationId);
                setNewFolderName('');
              }}
            >
              {t.folder.new}
            </AddButton>
          </div>
        )}
      </div>
    );
  };

  // Componente de header de seção reutilizável
  const renderSectionHeader = (
    sectionKey: string,
    label: string,
    count: number,
    options?: { icon?: React.ReactNode; showPlus?: boolean; limitBadge?: string }
  ) => (
    <div
      className={cn("flex items-center justify-between text-[length:var(--font-xs)] font-semibold uppercase text-white/50 tracking-[0.5px] cursor-pointer select-none hover:bg-white/5", collapsed ? "justify-center p-2" : "px-4 py-2")}
      onClick={() => !collapsed && toggleSectionState(sectionKey)}
    >
      {!collapsed && (
        <>
          <div className="flex items-center gap-1.5">
            {options?.icon}
            <span>{label}</span>
          </div>
          {options?.limitBadge ? (
            <span className={cn(COUNT_BADGE_CLASS, projectCount >= projectLimit && "text-[var(--color-danger)] bg-[rgba(239,68,68,0.15)]")}>
              {options.limitBadge}
            </span>
          ) : (
            <span className={COUNT_BADGE_CLASS}>{count}</span>
          )}
        </>
      )}
      <div className="flex items-center gap-0.5 ml-auto">
        {options?.showPlus && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid="sidebar-new-project"
                aria-label={t.project.new}
                className="bg-transparent border-none text-white/50 cursor-pointer p-1 rounded-[var(--radius-sm)] flex items-center hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]"
                onClick={(e) => { e.stopPropagation(); handleNewProjectClick(); }}
              >
                <Plus size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right"><p>{t.project.new}</p></TooltipContent>
          </Tooltip>
        )}
        {!collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="bg-transparent border-none text-white/40 cursor-pointer p-0.5 rounded-[var(--radius-sm)] flex items-center justify-center hover:text-white/80">
                {isSectionExpanded(sectionKey) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top"><p>{isSectionExpanded(sectionKey) ? t.nav.collapse : t.nav.expand}</p></TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={300}>
      {/* Backdrop mobile */}
      {mobileSidebarOpen && (
        <div
          className="hidden max-md:block fixed inset-0 bg-black/50 z-[99] transition-opacity"
          onClick={onMobileClose}
        />
      )}
      <aside {...atributosInertes} aria-hidden={gavetaForaDeAlcance || undefined} data-tour="sidebar" role="navigation" aria-label={t.nav.ariaSidebar} className={cn("flex flex-col h-full bg-[var(--sidebar-bg)] text-[var(--sidebar-text)] shrink-0 transition-[width,transform] duration-200 ease-in-out", collapsed ? "w-[60px]" : "w-[260px]", "max-md:fixed max-md:left-0 max-md:top-0 max-md:z-[100] max-md:pt-[env(safe-area-inset-top)] max-md:shadow-[2px_0_10px_rgba(0,0,0,0.3)] max-md:h-full max-md:!w-[260px]", mobileSidebarOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full")}>
        {/* ═══ ZONA 1: Header + Nav global (FIXA) ═══ */}
        <div className="shrink-0">
          {/* Header */}
          <div className={cn("flex items-center border-b border-[var(--sidebar-border)]", collapsed ? "justify-center px-2 py-4" : "justify-between p-4")}>
            {!collapsed && (
              <div className="flex items-center gap-2.5 text-[var(--font-lg)] font-semibold text-[var(--sidebar-text)] [&_img]:w-7 [&_img]:h-7">
                <img src="/logo.svg" alt="MeuGantt" />
                <span>MeuGantt</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              {/* Fechar a gaveta, so no celular. Ate aqui o unico jeito de sair
                  era tocar fora, que e gesto que ninguem descobre sozinho: nao
                  ha nada na tela dizendo que aquela faixa preta e clicavel. */}
              <button
                data-testid="sidebar-mobile-close"
                className="hidden max-md:flex items-center justify-center w-8 h-8 rounded-[var(--radius-md)] text-[var(--sidebar-text-muted)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]"
                onClick={onMobileClose}
                aria-label={t.nav.closeMenu}
              >
                <X size={18} />
              </button>
              <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="bg-transparent border-none text-[var(--sidebar-text-muted)] cursor-pointer p-1.5 rounded-[var(--radius-sm)] flex items-center justify-center hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]"
                  onClick={() => setCollapsed(!collapsed)}
                >
                  {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{collapsed ? t.nav.expand : t.nav.collapse}</p>
              </TooltipContent>
            </Tooltip>
            </div>
          </div>

          {/* Navegação global */}
          <div className="py-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={cn("flex items-center gap-3 w-full bg-transparent border-none text-white/80 text-[length:var(--font-base)] cursor-pointer text-left transition-all duration-150 ease-in-out hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]", collapsed ? "justify-center p-2.5" : "px-4 py-2.5", currentModule === 'home' && "bg-[var(--sidebar-active)] border-l-3 border-l-[var(--sidebar-accent)] text-[var(--sidebar-text)] [&_svg]:text-[var(--sidebar-accent)]")}
                  data-testid="nav-home"
                  onClick={() => handleModuleClick('home')}
                >
                  <LayoutDashboard size={20} />
                  {!collapsed && <span>{t.modules.home}</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right"><p>{t.modules.home}</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-tour="search"
                  className={cn("flex items-center gap-3 w-full bg-transparent border-none text-white/80 text-[length:var(--font-base)] cursor-pointer text-left transition-all duration-150 ease-in-out hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]", collapsed ? "justify-center p-2.5" : "px-4 py-2.5")}
                  onClick={onOpenSearch}
                >
                  <Search size={20} />
                  {!collapsed && <span>{t.nav.search}</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right"><p>{t.nav.searchHint}</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-tour="premium-teaser"
                  className={cn("flex items-center gap-3 w-full bg-transparent border-none text-white/80 text-[length:var(--font-base)] cursor-pointer text-left transition-all duration-150 ease-in-out hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]", collapsed ? "justify-center p-2.5" : "px-4 py-2.5", currentModule === 'portfolio' && "bg-[var(--sidebar-active)] border-l-3 border-l-[var(--sidebar-accent)] text-[var(--sidebar-text)] [&_svg]:text-[var(--sidebar-accent)]", isModuleLocked('portfolio') && "opacity-60")}
                  data-testid="nav-portfolio"
                  onClick={() => handleModuleClick('portfolio')}
                >
                  <Briefcase size={20} />
                  {!collapsed && <span>{t.modules.portfolio}</span>}
                  {isModuleLocked('portfolio') && !collapsed && <Lock size={14} className="ml-auto text-white/40" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{isModuleLocked('portfolio') ? fill(t.nav.lockedHint, { module: t.modules.portfolio, plan: 'Professional' }) : t.modules.portfolio}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={cn("flex items-center gap-3 w-full bg-transparent border-none text-white/80 text-[length:var(--font-base)] cursor-pointer text-left transition-all duration-150 ease-in-out hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]", collapsed ? "justify-center p-2.5" : "px-4 py-2.5", currentModule === 'mytasks' && "bg-[var(--sidebar-active)] border-l-3 border-l-[var(--sidebar-accent)] text-[var(--sidebar-text)] [&_svg]:text-[var(--sidebar-accent)]")}
                  data-testid="nav-mytasks"
                  onClick={() => handleModuleClick('mytasks')}
                >
                  <ListChecks size={20} />
                  {!collapsed && <span>{t.modules.mytasks}</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t.modules.mytasks}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* ═══ ZONA 2: Projetos (SCROLLA) ═══ */}
        <div className="flex-1 overflow-y-auto border-t border-[var(--sidebar-border)] py-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}>
          {/* Convites pendentes */}
          {!collapsed && <OrgInviteBanner />}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center gap-2 p-4 text-white/50 text-[length:var(--font-sm)]">
              <Loader2 size={20} className="animate-spin" />
              {!collapsed && <span>{t.nav.loading}</span>}
            </div>
          )}

          {/* Colapsado: botão "+" único no topo */}
          {collapsed && !loading && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="sidebar-new-project"
                  aria-label={t.project.new}
                  className="flex items-center justify-center w-full p-2.5 bg-transparent border-none text-white/60 cursor-pointer hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-accent)] transition-all duration-150"
                  onClick={handleNewProjectClick}
                >
                  <Plus size={20} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right"><p>{t.project.new}</p></TooltipContent>
            </Tooltip>
          )}

          {/* ★ FAVORITOS — só aparece se houver */}
          {!loading && favoriteProjects.length > 0 && (
            <div className="mb-1">
              {!collapsed && renderSectionHeader('favorites', t.nav.favorites, favoriteProjects.length, {
                icon: <Star size={12} className="text-[var(--color-warning)]" />
              })}
              {/* Favoritos fica LISO de proposito: e um atalho que atravessa
                  todos os escopos (pessoal, compartilhado, cada organizacao), e
                  reproduzir as pastas aqui dentro seria mostrar a mesma arvore
                  duas vezes. O projeto aparece aqui E no lugar onde mora. */}
              {(isSectionExpanded('favorites') || collapsed) && (
                <div className="flex flex-col">
                  {favoriteProjects.map(p => renderProjectItem(p))}
                </div>
              )}
            </div>
          )}

          {/* MEUS PROJETOS */}
          {!loading && (
            <div className="mb-1">
              {!collapsed && renderSectionHeader('personal', t.nav.myProjects, personalProjects.length, {
                showPlus: true,
                limitBadge: showProjectLimit ? `${projectCount}/${projectLimit}` : undefined
              })}

              {showNewProjectInput && !collapsed && (
                <div className="flex items-center gap-2 px-4 py-2">
                  <Input
                    type="text"
                    placeholder={t.nav.projectNamePlaceholder}
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateProject();
                      if (e.key === 'Escape') {
                        setShowNewProjectInput(false);
                        setNewProjectName('');
                      }
                    }}
                    autoFocus
                    className="flex-1 h-auto px-2.5 py-2 bg-white/10 border-white/20 text-[var(--sidebar-text)] text-[length:var(--font-sm)] placeholder:text-white/40 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--sidebar-accent)]"
                  />
                  <Button variant="ghost" className="p-2 bg-[var(--sidebar-accent)] rounded-[var(--radius-sm)] text-[var(--sidebar-text)] hover:bg-[var(--color-primary)] disabled:opacity-60 disabled:cursor-not-allowed" onClick={handleCreateProject} disabled={saving}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  </Button>
                </div>
              )}

              {(isSectionExpanded('personal') || collapsed) && (
                <div className="flex flex-col">
                  {personalProjects.length === 0 && !collapsed ? (
                    <div className="flex items-center justify-center gap-2 p-4 text-white/50 text-[length:var(--font-sm)]">
                      <span>{t.nav.noProjects}</span>
                    </div>
                  ) : collapsed ? (
                    personalProjects.map(p => renderProjectItem(p))
                  ) : (
                    renderScopeProjects(personalProjects, null)
                  )}
                </div>
              )}
            </div>
          )}

          {/* PROJETOS COMPARTILHADOS */}
          {!loading && sharedProjects.length > 0 && (
            <div className="mb-1">
              {!collapsed && renderSectionHeader('shared', t.nav.shared, sharedProjects.length, {
                icon: <FolderInput size={12} className="text-white/40" />
              })}
              {(isSectionExpanded('shared') || collapsed) && (
                <div className="flex flex-col">
                  {sharedProjects.map(p => renderProjectItem(p))}
                </div>
              )}
            </div>
          )}

          {/* Seções por Organização */}
          {!loading && activeOrgs.map(org => {
            const orgProjects = getOrgProjects(org.id);
            if (orgProjects.length === 0) return null;
            return (
              <div key={org.id} className="mb-1">
                {!collapsed && renderSectionHeader(`org_${org.id}`, org.name, orgProjects.length, {
                  icon: <Building2 size={12} className="text-white/40" />
                })}
                {(isSectionExpanded(`org_${org.id}`) || collapsed) && (
                  <div className="flex flex-col">
                    {collapsed
                      ? orgProjects.map(p => renderProjectItem(p))
                      : renderScopeProjects(orgProjects, org.id)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ═══ ZONA 3: Footer (FIXA) ═══
            Eram CINCO linhas empilhadas (Ajuda, Notificações, Tema,
            Configurações, Sair) comendo ~180px do rodapé para abrigar ações que
            o usuário usa uma vez por semana, enquanto a lista de projetos, que
            é o trabalho, disputava o resto. Vira uma linha de conta com menu,
            como fazem Claude, Linear e Notion.

            O sino fica FORA do menu, ao lado: contador de não lidas dentro de
            menu fechado é contador invisível. */}
        <div className="shrink-0 border-t border-[var(--sidebar-border)] p-2">
          <TrialBanner collapsed={collapsed} />
          <div className={cn('flex items-center gap-1', collapsed && 'flex-col gap-2')}>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="sidebar-account"
                      aria-label={accountName}
                      className={cn(
                        'flex items-center gap-2 rounded-[var(--radius-md)] bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:bg-[var(--sidebar-hover)]',
                        collapsed ? 'h-9 w-9 justify-center' : 'h-11 min-w-0 flex-1 px-2'
                      )}
                    >
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                        style={{ backgroundColor: avatarColorFor(profile?.id || profile?.email || 'meugantt') }}
                      >
                        {initialsFor(accountName)}
                      </span>
                      {!collapsed && (
                        <>
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-[length:var(--font-sm)] text-white/90">{accountName}</span>
                            <span className="truncate text-[length:var(--font-xs)] text-white/40">{accountPlan}</span>
                          </span>
                          {/* Duas pontas, como o bloco de conta do shadcn: diz
                              "abre menu" sem depender de estado. Um chevron
                              unico parecia indicador de "role para cima". */}
                          <ChevronsUpDown size={14} className="shrink-0 text-white/40" />
                        </>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{accountName}</p></TooltipContent>
              </Tooltip>
              <DropdownMenuContent side="top" align="start" className="w-[232px]">
                <DropdownMenuLabel className="truncate font-normal text-[length:var(--font-xs)] text-[var(--color-text-muted)]">
                  {profile?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleModuleClick('settings')}>
                  <Settings size={14} className="mr-2" /> {t.nav.settings}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}>
                  {resolvedTheme === 'dark'
                    ? <Sun size={14} className="mr-2" />
                    : <Moon size={14} className="mr-2" />}
                  {resolvedTheme === 'dark' ? t.nav.themeLight : t.nav.themeDark}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleModuleClick('wiki')}>
                  <BookOpen size={14} className="mr-2" /> {t.nav.knowledgeBase}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSupportOpen(true)}>
                  <MessageCircle size={14} className="mr-2" /> {t.nav.contactSupport}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-[var(--color-danger)] focus:text-[var(--color-danger)]"
                  onClick={logout}
                >
                  <LogOut size={14} className="mr-2" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <NotificationBell
              variant="icon"
              onNavigateToTask={(projectId, taskId) => {
                // Fica no módulo atual se ele já renderiza tarefas; senão vai
                // para o Gantt, que é o módulo principal do produto. Mandar
                // sempre para o Kanban tirava o usuário de onde ele estava.
                if (!MODULOS_COM_TAREFA.has(currentModule)) changeModule('gantt');
                // Clicar numa notificacao e navegar: a gaveta sai da frente.
                onMobileClose?.();
                focusTask(projectId, taskId);
              }}
            />
          </div>
        </div>
      </aside>

      {/* Modal compacto de upgrade */}
      <Dialog open={upgradeModal !== null} onOpenChange={(open) => !open && setUpgradeModal(null)}>
        <DialogContent className="max-w-sm text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-4 text-[var(--color-primary)]">
            <Lock size={28} />
          </div>
          <DialogHeader className="text-center">
            <DialogTitle>{upgradeModal?.displayName}</DialogTitle>
            <DialogDescription>{upgradeModal?.description}</DialogDescription>
          </DialogHeader>
          <div className="inline-block px-3 py-1.5 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded-full text-sm font-medium">
            {tShell.availableInPlan} <strong>{upgradeModal?.minPlan}</strong>
          </div>
          <DialogFooter className="justify-center gap-3 sm:justify-center">
            <Button variant="outline" onClick={() => setUpgradeModal(null)}>{t.nav.close}</Button>
            <Button onClick={() => { analytics.trackPlanUpgradeClick('sidebar'); setUpgradeModal(null); setShowPricing(true); }}>
              <ArrowUpCircle size={16} />
              {tShell.upgrade}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PricingPage */}
      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}

      <ConfirmModal
        isOpen={confirmDeleteProject !== null}
        title={t.nav.deleteProject}
        message={tShell.confirmDeleteProject}
        confirmLabel={tShell.confirmDeleteLabel}
        variant="danger"
        onConfirm={executeDeleteProject}
        onCancel={() => setConfirmDeleteProject(null)}
      />

      {/* Excluir pasta nao e excluir projeto, e a mensagem precisa dizer isso:
          o medo de perder a carteira inteira e o que faz o usuario nunca mais
          mexer numa pasta. */}
      <ConfirmModal
        isOpen={confirmDeleteFolder !== null}
        title={t.folder.delete}
        message={fill(t.folder.confirmDelete, { name: confirmDeleteFolder?.name || '' })}
        confirmLabel={tShell.confirmDeleteLabel}
        variant="danger"
        onConfirm={executeDeleteFolder}
        onCancel={() => setConfirmDeleteFolder(null)}
      />

      <ConfirmModal
        isOpen={confirmRemoveFromOrg !== null}
        title={t.nav.removeFromOrg}
        message={tShell.confirmRemoveFromOrg}
        confirmLabel={tShell.confirmRemoveLabel}
        variant="danger"
        onConfirm={executeRemoveFromOrg}
        onCancel={() => setConfirmRemoveFromOrg(null)}
      />

      {/* Modal de criação de projeto com IA */}
      {showAIModal && (
        <AIProjectModal
          onClose={() => setShowAIModal(false)}
        />
      )}

      {/* Modal de mover projeto para organização */}
      {shareProjectId && (
        <OrgProjectShareModal
          projectId={shareProjectId.id}
          projectName={shareProjectId.name}
          onClose={() => setShareProjectId(null)}
        />
      )}

      {/* Modal de Suporte */}
      <SupportPanel open={supportOpen} onOpenChange={setSupportOpen} />
    </TooltipProvider>
  );
};

export default Sidebar;
